import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  getAllTerminals,
  getCargoBarChart,
  getRevenueBarChart,
  getVehicleAction,
  getVehicleBarChart,
  getVehicleComparison,
  getVehicleLevel4TableData,
  getYardBarChart,
  getHQSettings,
  createHQSettings,
  updateHQSettings,
} from "./actions/hqDashboardActions";

// Define a type for the slice state
interface hqDashboardState {
  loader: true | false;
  vehicleComparisonData: any[];
  vehicleLevel4Data: any[];
  allBorderTerminals: any[];
  selectedBorderTerminals: any[];
  startDate: string;
  endDate: string;
}
const initialState: hqDashboardState = {
  loader: false,
  vehicleComparisonData: [],
  vehicleLevel4Data: [],
  allBorderTerminals: [],
  selectedBorderTerminals: [],
  startDate: "",
  endDate: "",
};

export const hqDashboardSlice = createSlice({
  name: "dashboard",
  initialState: initialState,
  reducers: {
    setSelectedBorderTerminalValues: (state: any, value) => {
      state.selectedBorderTerminals = value.payload;
    },
    setStartDate: (state: any, value) => {
      state.startDate = value.payload;
    },
    setEndDate: (state: any, value) => {
      state.endDate = value.payload;
    },
  },
  extraReducers: (builder) => {
    // handle Asynchronous Calls
    // Get getAllTerminals methods
    builder.addCase(getAllTerminals.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getAllTerminals.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getAllTerminals.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.allBorderTerminals = payload.borderTerminals;
    });
    // ./getAllTerminals methods

    // Get getVehicleAction methods
    builder.addCase(getVehicleAction.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getVehicleAction.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getVehicleAction.fulfilled, (state, { payload }) => {
      state.loader = false;
    });
    // ./getVehicleAction methods

    // Get getVehicleBarChart methods
    builder.addCase(getVehicleBarChart.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getVehicleBarChart.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getVehicleBarChart.fulfilled, (state, { payload }) => {
      state.loader = false;
    });
    // ./getVehicleBarChart methods

    // Get getCargoBarChart methods
    builder.addCase(getCargoBarChart.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getCargoBarChart.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getCargoBarChart.fulfilled, (state, { payload }) => {
      state.loader = false;
    });
    // ./getCargoBarChart methods

    // Get getRevenueBarChart methods
    builder.addCase(getRevenueBarChart.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getRevenueBarChart.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getRevenueBarChart.fulfilled, (state, { payload }) => {
      state.loader = false;
    });
    // ./getRevenueBarChart methods

    // Get getYardBarChart methods
    builder.addCase(getYardBarChart.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getYardBarChart.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getYardBarChart.fulfilled, (state, { payload }) => {
      state.loader = false;
    });
    // ./getYardBarChart methods

    // Get getVehicleComparison methods
    builder.addCase(getVehicleComparison.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getVehicleComparison.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getVehicleComparison.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.vehicleComparisonData = payload.vehicleConsignmentComparison;
    });
    // ./getVehicleComparison methods

    // Get getVehicleLevel4TableData methods
    builder.addCase(getVehicleLevel4TableData.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getVehicleLevel4TableData.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getVehicleLevel4TableData.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.vehicleLevel4Data = payload.vehicleConsignmentCargoDetailList;
    });
    // ./getVehicleLevel4TableData methods

    // Get getHQSettings methods
    builder.addCase(getHQSettings.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getHQSettings.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getHQSettings.fulfilled, (state) => {
      state.loader = false;
    });

    //  POST createHQSettings method
    builder.addCase(createHQSettings.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(createHQSettings.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(createHQSettings.fulfilled, (state) => {
      state.loader = false;
    });

    //  POST updateHQSettings methods
    builder.addCase(updateHQSettings.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(updateHQSettings.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(updateHQSettings.fulfilled, (state) => {
      state.loader = false;
    });
  },
});

export const { setSelectedBorderTerminalValues, setStartDate, setEndDate } =
  hqDashboardSlice.actions;

export const loader = (state: RootState) => state.dashboard.loader;
export const vehicleComparisonData = (state: RootState) => state.dashboard.vehicleComparisonData;
export const vehicleLevel4Data = (state: RootState) => state.dashboard.vehicleLevel4Data;
export const selectedBorderTerminals = (state: RootState) =>
  state.dashboard.selectedBorderTerminals;
export const selectedStartDate = (state: RootState) => state.dashboard.startDate;
export const selectedEndDate = (state: RootState) => state.dashboard.endDate;
export const allBorderTerminals = (state: RootState) => state.dashboard.allBorderTerminals;
